import { useMemo } from 'react';

import useAdminInvoiceSWR from 'hooks/useAdminInvoiceSWR';
import useInvoicePreview from 'hooks/useInvoicePreview';
import { InvoiceApprovalStatus, InvoiceStatus } from 'lib/constants';
import { IInvoiceInfo } from 'types';
import { getOpenInvoiceCounts } from 'utils/invoice';

interface UseInvoicesProps {
  companyID: string;
  /** When set, both list fetches are scoped to this contractor. */
  contractorUserId?: string;
  /** Feeds the overdue/due-window count logic. */
  companyInvoiceAvailabilityDaysCount?: number;
  /**
   * Optional per-invoice resolver for the invoice-availability window. When set, takes precedence over
   * `companyInvoiceAvailabilityDaysCount` per invoice. Used to honor team-level overrides.
   */
  getDaysForInvoice?: (invoiceInfo: IInvoiceInfo) => number | undefined;
  /** Pass false to disable fetching entirely (e.g. non-admin POV). Default true. */
  isAdminPoV?: boolean;
  /** Fetch bulk-pay preview for the open+unapproved set (Review page needs this). */
  withUnapprovedPreview?: boolean;
  /** Fetch bulk-pay preview for the open+approved set (Pay-Approved page needs this). */
  withApprovedPreview?: boolean;
}

/**
 * Single source of truth for a company's OPEN invoices, split by approval status.
 *
 * Both lists come from GSI-backed queries via useAdminInvoiceSWR, so every
 * useInvoices consumer for the same company shares the same two SWR caches.
 * Mutations should go through invoiceMutations, which updates these caches
 * optimistically — that is what keeps the derived previews from going stale.
 */
export default function useInvoices({
  companyID,
  contractorUserId,
  companyInvoiceAvailabilityDaysCount,
  getDaysForInvoice,
  isAdminPoV = true,
  withUnapprovedPreview = false,
  withApprovedPreview = false,
}: UseInvoicesProps) {
  const unapproved = useAdminInvoiceSWR({
    companyID,
    status: InvoiceStatus.OPEN,
    approvalStatus: InvoiceApprovalStatus.UNAPPROVED,
    contractorUserId,
    isAdminPoV,
  });

  const approved = useAdminInvoiceSWR({
    companyID,
    status: InvoiceStatus.OPEN,
    approvalStatus: InvoiceApprovalStatus.APPROVED,
    contractorUserId,
    isAdminPoV,
  });

  const allOpenUnapprovedInvoices = useMemo(() => unapproved.invoices || [], [unapproved.invoices]);
  const allOpenApprovedInvoices = useMemo(() => approved.invoices || [], [approved.invoices]);

  const allOpenInvoices = useMemo(
    () => [...allOpenUnapprovedInvoices, ...allOpenApprovedInvoices],
    [allOpenUnapprovedInvoices, allOpenApprovedInvoices],
  );

  const countStats = useMemo(
    () => getOpenInvoiceCounts(allOpenInvoices, companyInvoiceAvailabilityDaysCount, getDaysForInvoice),
    [allOpenInvoices, companyInvoiceAvailabilityDaysCount, getDaysForInvoice],
  );

  const unapprovedPreview = useInvoicePreview(
    companyID,
    allOpenUnapprovedInvoices,
    withUnapprovedPreview && !(unapproved.isValidating && !unapproved.isPending),
    companyInvoiceAvailabilityDaysCount,
    getDaysForInvoice,
  );
  const approvedPreview = useInvoicePreview(
    companyID,
    allOpenApprovedInvoices,
    withApprovedPreview && !(approved.isValidating && !approved.isPending),
    companyInvoiceAvailabilityDaysCount,
    getDaysForInvoice,
  );

  // Undefined until both previews have resolved, so pages can keep gating their first-load skeleton
  // on it the same way they do for a single bucket.
  const allOpenInvoicesWithPreview = useMemo(() => {
    const unapprovedRows = unapprovedPreview.invoicesWithPreviewData;
    const approvedRows = approvedPreview.invoicesWithPreviewData;
    if (!unapprovedRows || !approvedRows) return undefined;
    return [...unapprovedRows, ...approvedRows];
  }, [unapprovedPreview.invoicesWithPreviewData, approvedPreview.invoicesWithPreviewData]);

  return {
    allOpenInvoices,
    openInvoicesCount: countStats.openInvoicesCount,
    approvedInvoicesCount: countStats.approvedInvoicesCount,
    overdueButApprovedCount: countStats.overdueButApprovedCount,
    overdueButNotApprovedCount: countStats.overdueButNotApprovedCount,
    unapproved: {
      invoices: allOpenUnapprovedInvoices,
      invoicesWithPreview: unapprovedPreview.invoicesWithPreviewData,
      openAmount: unapprovedPreview.openAmount,
      totalAmount: unapprovedPreview.totalAmount,
      totalSubscriptionAmount: unapprovedPreview.totalSubscriptionAmount,
      totalAmountWithSubscription: unapprovedPreview.totalAmountWithSubscription,
      isListPending: unapproved.isPending,
      isListValidating: unapproved.isValidating,
      isPreviewPending: unapprovedPreview.isPending,
      previewError: unapprovedPreview.error,
    },
    approved: {
      invoices: allOpenApprovedInvoices,
      invoicesWithPreview: approvedPreview.invoicesWithPreviewData,
      totalAmount: approvedPreview.totalAmount,
      totalSubscriptionAmount: approvedPreview.totalSubscriptionAmount,
      totalAmountWithSubscription: approvedPreview.totalAmountWithSubscription,
      isListPending: approved.isPending,
      isListValidating: approved.isValidating,
      isPreviewPending: approvedPreview.isPending,
      previewError: approvedPreview.error,
    },
    /** Both buckets, for pages filtered on something orthogonal to approval. Needs both preview flags on. */
    allOpen: {
      invoices: allOpenInvoices,
      invoicesWithPreview: allOpenInvoicesWithPreview,
      totalAmount: (unapprovedPreview.totalAmount ?? 0) + (approvedPreview.totalAmount ?? 0),
      isListPending: unapproved.isPending || approved.isPending,
      isListValidating: unapproved.isValidating || approved.isValidating,
      isPreviewPending: unapprovedPreview.isPending || approvedPreview.isPending,
      previewError: unapprovedPreview.error ?? approvedPreview.error,
    },
  };
}
